import { Divider, hideScrollBar, Toast } from '@byecode/ui'
import type { GroupPage, PageAbstract } from '@lighthouse/core'
import { PageOpenType, PageStruct } from '@lighthouse/core'
import { floatPageTree, useAtomAction, useAtomData } from '@lighthouse/shared'
import { filter } from 'rambda'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import { useLatest, useUpdateEffect } from 'react-use'
import styled from 'styled-components'
import { useImmer } from 'use-immer'

import { AppSystemPageAtom, languageAtom } from '@/atoms/application/state'
import {
    clonePageAtom,
    createPageAtom,
    movePageAtom,
    openPageStackAtom,
    removePageAtom,
    updateHomePageAtom,
    updatePageAtom
} from '@/atoms/page/action'
import { lastPageOfStackAtom, pageTreeListAtom } from '@/atoms/page/state'
import { type CreatePagePayload } from '@/atoms/page/types'
import { stackFactory } from '@/atoms/page/utils'
import { findByPageByTree } from '@/atoms/utils/page'
import { useDataSourceRoles } from '@/hooks/useDataSource'
import { useCurrentLanguagePageList } from '@/hooks/usePage'

import { PAGE_FILTER_OPTIONS } from '../constant'
import type { FilterParams } from '../PageListHeader'
import PageListHeader from '../PageListHeader'
import { CustomRouteModal } from './CustomRouteModal'
import { PageEmpty } from './Empty'
import { DragList } from './List/DragList'
import { SearchPageList } from './List/SearchPage'
import { SystemPage } from './SystemPage'

const PageListContainer = styled.div`
    width: 100%;
    flex: 1;
    padding: 0px 12px;
    overflow: hidden;
`

const PageListWrapper = styled.div`
    width: 100%;
    height: 100%;
    padding-bottom: 8px;
    display: flex;
    flex-direction: column;
    gap: 8px;
`

interface PageListProps {}

const LOGIN_PAGE: PageAbstract = { ...PageStruct(), id: 'LOGIN', name: '登录页面' }

export const PageList: React.FC<PageListProps> = () => {
    const [filterParams, setFilterParams] = useImmer<FilterParams>({
        searchWord: '',
        groupId: 'all'
    })
    const { searchWord, groupId } = filterParams
    const [focusPage, setFocusPage] = useState('')
    const pageListContainerRef = useRef<HTMLDivElement>(null)

    const { run: openPageStack } = useAtomAction(openPageStackAtom)
    const { run: updatePage } = useAtomAction(updatePageAtom)
    const { run: removePage } = useAtomAction(removePageAtom)
    const { run: movePage } = useAtomAction(movePageAtom)
    const { run: updateHomePage } = useAtomAction(updateHomePageAtom)
    const { run: createPage } = useAtomAction(createPageAtom)
    const { run: clonePage } = useAtomAction(clonePageAtom)
    const { run: setAppSystemPage } = useAtomAction(AppSystemPageAtom)

    const pageId = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => s?.pageId, [])
    )
    const appSystemPage = useAtomData(AppSystemPageAtom)
    const currentLanguage = useAtomData(languageAtom)
    const appGroups = useDataSourceRoles()
    const pageTreeList = useAtomData(pageTreeListAtom)

    const floatPageTrees = useMemo(() => {
        return floatPageTree(pageTreeList, [])
    }, [pageTreeList])

    const floatGroupsIdsRef = useLatest(
        floatPageTrees.reduce<string[]>((acc, v) => {
            if (v.type === 'GROUP') {
                acc.push(v.id)
            }
            return acc
        }, [])
    )
    const [openMap, setOpenMap] = useImmer<Record<string, boolean>>(
        Object.fromEntries(floatPageTrees.filter(v => v.type === 'GROUP').map(v => [v.id, true]))
    )

    const filterFloatPageTrees = useMemo(() => {
        return filter(
            ({ type, parentId, language }) => (parentId ? openMap[parentId] : true) && currentLanguage === language,
            floatPageTrees
        )
    }, [currentLanguage, floatPageTrees, openMap])

    const roleOptions = useMemo(
        () => [...PAGE_FILTER_OPTIONS, ...appGroups.map(({ id, name }) => ({ label: `${name.toString()}可见`, value: id, group: 'user' }))],
        [appGroups]
    )
    const langPageList = useCurrentLanguagePageList()

    const currentGroupPageList = useMemo(() => {
        switch (groupId) {
            case 'all': {
                return langPageList
            }
            case 'public': {
                return filter(({ open }) => open === PageOpenType.all, langPageList)
            }
            case 'login': {
                return filter(({ open }) => open === PageOpenType.login, langPageList)
            }
            default: {
                return filter(({ roleIds = [] }) => roleIds.includes(groupId), langPageList)
            }
        }
    }, [groupId, langPageList])

    const filterPageList = useMemo(() => {
        return filter(({ name }) => name.includes(searchWord), currentGroupPageList)
    }, [currentGroupPageList, searchWord])

    const activeId = useMemo(() => appSystemPage ?? pageId, [appSystemPage, pageId])

    const handlePageCopy = useCallback(
        async (id: string, lang: string, groupId?: string) => {
            const pageId = await clonePage({ id, lang, groupId })
            Toast.success('复制成功')
            // lang === currentLanguage && openPageStack(stackFactory({ pageId }))
            // setAppSystemPage(null)
            return pageId
        },
        [clonePage]
    )

    const handlePageAdd = useCallback(
        async (v: CreatePagePayload) => {
            const pageId = await createPage(v)
            if (v.type === 'COMMON') {
                openPageStack(stackFactory({ pageId }))
                setAppSystemPage(null)
                setFocusPage(pageId)
            }
        },
        [createPage, openPageStack, setAppSystemPage]
    )

    const handleToLink = useCallback(
        (page: PageAbstract) => {
            const { id: jumpPageId } = page
            if ((!jumpPageId || pageId === jumpPageId) && !appSystemPage) {
                return
            }
            setAppSystemPage(null)
            openPageStack(stackFactory({ pageId: jumpPageId }))
        },
        [appSystemPage, openPageStack, pageId, setAppSystemPage]
    )

    useUpdateEffect(() => {
        const groups = floatPageTrees.filter(v => v.type === 'GROUP')
        setOpenMap(draft => {
            groups.forEach(group => {
                if (draft[group.id] === undefined) {
                    draft[group.id] = true
                }
            })
        })
    }, [floatPageTrees])

    const handleChangeEdit = useCallback((id: string) => {
        setFocusPage(v => {
            return v === id ? '' : id
        })
    }, [])

    const [customRouteEditPage, setCustomRouteEditPage] = useState<PageAbstract | null>(null)
    const handleCustomRoute = useCallback(
        (id: string) => {
            const page = langPageList.find(item => item.id === id)
            if (!page) {
                return
            }
            setCustomRouteEditPage(page)
        },
        [langPageList]
    )
    const handleUpdatePageRoute = useCallback(
        (data: { id: string; route: string | undefined }) => {
            return updatePage(data)
        },
        [updatePage]
    )

    const handleToggleCollapse = useCallback(
        (id: string) => {
            const group = findByPageByTree(pageTreeList)(id) as GroupPage | undefined
            const groups = group ? [group, ...floatPageTree(group.children, []).filter(v => v.type === 'GROUP')] : []
            if (!group) {
                return
            }
            setOpenMap(draft => {
                const open = !draft[group.id]
                groups.forEach(({ id }) => {
                    draft[id] = open
                })
            })
        },
        [pageTreeList, setOpenMap]
    )

    const handleAllToggleCollapse = useCallback(
        (open: boolean) => {
            setOpenMap(Object.fromEntries(floatGroupsIdsRef.current.map(id => [id, open])))
        },
        [floatGroupsIdsRef, setOpenMap]
    )

    return (
        <>
            <PageListHeader value={filterParams} options={roleOptions} onAddPage={handlePageAdd} onChange={setFilterParams} />
            <PageListContainer ref={pageListContainerRef}>
                <PageListWrapper>
                    <div style={{ flex: 1, overflow: 'hidden' }}>
                        {filterPageList.length === 0 ? (
                            <PageEmpty />
                        ) : searchWord === '' && groupId === 'all' ? (
                            <DragList
                                list={filterFloatPageTrees}
                                openedMap={openMap}
                                onToggleCollapse={handleToggleCollapse}
                                focusPage={focusPage}
                                activePageId={activeId}
                                onSetHomePage={updateHomePage}
                                onToLink={handleToLink}
                                onUpdatePage={updatePage}
                                onCopyPage={handlePageCopy}
                                onRemovePage={removePage}
                                onMovePage={movePage}
                                onAddPage={handlePageAdd}
                                onChangeEdit={handleChangeEdit}
                                onCustomRoute={handleCustomRoute}
                                onAllToggleCollapse={handleAllToggleCollapse}
                            />
                        ) : (
                            <SearchPageList list={filterPageList} activeId={activeId} onToLink={handleToLink} />
                        )}
                    </div>
                    <Divider />
                    <SystemPage data={LOGIN_PAGE} id={activeId} />
                </PageListWrapper>
            </PageListContainer>
            {customRouteEditPage && (
                <CustomRouteModal
                    open
                    onClose={() => {
                        setCustomRouteEditPage(null)
                    }}
                    page={customRouteEditPage}
                    pages={langPageList}
                    // onClear={() => handleUpdatePageRoute({ id: customRouteEditPage.id, route: '' })}
                    onConfirm={(route: string) => handleUpdatePageRoute({ id: customRouteEditPage.id, route })}
                />
            )}
        </>
    )
}
